/**
 * visitor.ts — 公開ページ向け永続 visitor ID ユーティリティ。
 *
 * 目的: 匿名訪問者の行動を後から顕名 contact に紐付ける（retroactive backfill）。
 *   1. 公開 LP / フォームの初回アクセス時に localStorage に `vid_<random>` を発行
 *   2. recordLandingView / submitForm でサーバーに送信し audience_events.visitorId に記録
 *   3. フォーム送信などで email → contactId が確定したら、Cloud Function 側で
 *      同 visitorId を持つ過去の audience_events に contactId を遡及補完
 *
 * 設計判断:
 *   - localStorage 限定（cookie ではない）：cross-domain は対象外、シンプルに保つ
 *   - SSR / 非ブラウザ環境では null を返す（recordView 側で null チェック）
 *   - 値は乱数 16byte = 22文字 base64url。crypto.randomUUID() でも代替可能
 *   - キー名は `goenhub_vid` で名前衝突を回避
 */
const STORAGE_KEY = 'goenhub_vid';

/**
 * 既存の visitor ID を読み出す。なければ生成して保存。
 * SSR・localStorage 不可環境では null を返す（fail-safe）。
 */
export function getOrCreateVisitorId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const existing = window.localStorage.getItem(STORAGE_KEY);
    if (existing && /^vid_[A-Za-z0-9_-]{8,40}$/.test(existing)) {
      return existing;
    }
    const fresh = generateVisitorId();
    window.localStorage.setItem(STORAGE_KEY, fresh);
    return fresh;
  } catch {
    // localStorage が無効（プライベートブラウジング厳格モード等）
    return null;
  }
}

/**
 * 値を読むだけ（書き込まない）。デバッグ・閲覧用。
 */
export function readVisitorId(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/**
 * vid_xxxxxxxxxxxxxxxxxxxxxx（22 文字）を生成。
 * crypto.getRandomValues が無い環境（非常に古い）は Math.random で fallback。
 */
function generateVisitorId(): string {
  const bytes = new Uint8Array(16);
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.getRandomValues === 'function'
  ) {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  // base64url（=パディング除去）
  const b64 = btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
  return `vid_${b64}`;
}
